import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { posts } from '../blogPosts';

const formatDate = (iso) => iso.replace(/-/g, '.');

const Page = styled.main`
  --post-title: var(--text-muted);
  --post-meta: var(--text-muted);
  --post-title-hover: var(--text);
  flex: 1 0 auto;
  background: var(--bg);
  color: var(--text);
`;

const Content = styled(motion.div)`
  max-width: 46rem;
  width: 100%;
  margin: 0 auto;
  padding: 5rem 2rem 5.5rem;

  @media (max-width: 640px) {
    padding: 3rem 1.25rem 4rem;
  }
`;

const Label = styled.h1`
  margin: 0 0 8px;
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.2;
  text-align: left;
  color: var(--text);
`;

const Description = styled.p`
  margin: 0 0 1.75rem;
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 1.55;
  text-align: left;
  color: var(--text-muted);
`;

const Row = styled.li`
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  min-width: 0;
  padding: 0.42rem 0;
  color: inherit;
  text-decoration: none;
  cursor: ${(p) => (p.$link ? 'pointer' : 'default')};
  transition: opacity 0.22s ease;

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 4px;
    border-radius: 2px;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    gap: 1rem;
    padding: 0.7rem 0;
  }
`;

const Title = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.4;
  color: var(--post-title);
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: color 0.22s ease;

  @media (max-width: 640px) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }

  ${Row}:hover &,
  ${Row}:focus-within & {
    color: var(--post-title-hover);
  }
`;

const Dots = styled.span`
  flex: 1 1 auto;
  min-width: 1.25rem;
  height: 4px;
  align-self: center;
  background-image: radial-gradient(circle, var(--post-meta) 1px, transparent 1.15px);
  background-size: 7px 4px;
  background-repeat: repeat-x;
  background-position: left center;

  @media (max-width: 640px) {
    display: none;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  min-width: 0;

  @media (hover: hover) and (pointer: fine) {
    &:hover ${Row} {
      opacity: 0.32;
    }

    &:hover ${Row}:hover,
    &:hover ${Row}:focus-within {
      opacity: 1;
    }
  }
`;

const DateText = styled.time`
  flex: 0 0 auto;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 1.4;
  color: var(--post-meta);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  @media (max-width: 640px) {
    margin-left: auto;
    padding-top: 0.12rem;
  }
`;

const PostRow = ({ post }) => {
  const inner = (
    <>
      <Title>{post.title}</Title>
      <Dots aria-hidden="true" />
      <DateText dateTime={post.date}>{formatDate(post.date)}</DateText>
    </>
  );

  const href = post.href || (post.slug ? `/blog/${post.slug}` : null);
  if (!href) return <Row>{inner}</Row>;

  if (/^https?:\/\//.test(href)) {
    return (
      <Row as="a" $link href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </Row>
    );
  }

  return (
    <Row as={Link} $link to={href}>
      {inner}
    </Row>
  );
};

const BlogPage = () => {
  const sorted = [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <Page>
      <Content
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <Label>Blog</Label>
        <Description>
          A place for things I’m still thinking about.
          <br />
          Nothing formal. Just notes I wanted to keep.
        </Description>
        <List>
          {sorted.map((post) => (
            <PostRow key={`${post.date}-${post.title}`} post={post} />
          ))}
        </List>
      </Content>
    </Page>
  );
};

export default BlogPage;
